import React, { useState, useEffect } from 'react';
import axios from 'axios';
import RoomGrid from './components/RoomGrid';
import Dashboard from './components/Dashboard';
import ReservationList from './components/ReservationList';
import RoomDetails from './components/RoomDetails';
import CheckInOut from './components/CheckInOut';
import NewReservation from './components/NewReservation';
import Login from './components/Login';
import RoomFilters from './components/RoomFilters';
import SimpleDashboardGrid from './components/SimpleDashboardGrid';
import Agenda from './components/Agenda';
import authService from './services/authService';

// Configuration de l'API
const API_BASE = '/api';

function App() {
  const [currentView, setCurrentView] = useState('dashboard');
  const [rooms, setRooms] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [preselectedRoomId, setPreselectedRoomId] = useState(null);
  const [totalRooms, setTotalRooms] = useState(180); // Default from .env config
  const [databaseError, setDatabaseError] = useState(null);

  // Vérifier l'authentification au démarrage
  useEffect(() => {
    const checkAuth = () => {
      if (authService.isAuthenticated()) {
        setIsAuthenticated(true);
        setUser(authService.getUser());
        loadData();
      } else {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Vérifier que l'utilisateur est toujours authentifié
      if (!authService.isAuthenticated()) {
        setIsAuthenticated(false);
        return;
      }

      // Vérifier et annuler automatiquement les réservations expirées (DRY - UX proactive)
      await checkAndCancelExpiredReservations();

      const [roomsRes, reservationsRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE}/rooms`),
        axios.get(`${API_BASE}/reservations`),
        axios.get(`${API_BASE}/stats/dashboard`)
      ]);

      setRooms(roomsRes.data);
      setReservations(reservationsRes.data);
      setStats(statsRes.data);
      setFilteredRooms(roomsRes.data); // Initialiser les chambres filtrées
      setTotalRooms(roomsRes.data.length); // Mettre à jour le nombre total de chambres dynamiquement
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);

      // Si erreur d'authentification, déconnecter
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        handleLogout();
      } else if (error.response && error.response.status === 503) {
        // Erreur de base de données
        const errorData = error.response.data;
        setDatabaseError({
          type: errorData.error,
          message: errorData.message,
          details: errorData.details
        });
        showAlert('Base de données non disponible', 'error');
      } else {
        showAlert('Erreur lors du chargement des données', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Vérifier et annuler automatiquement les réservations expirées (UX proactive)
  const checkAndCancelExpiredReservations = async () => {
    try {
      const user = authService.getUser();
      // Vérifier si l'utilisateur a les permissions (admin ou manager)
      if (user && (user.role === 'admin' || user.role === 'manager')) {
        const response = await axios.post(`${API_BASE}/reservations/cancel-expired`);

        // Afficher feedback UX seulement si des réservations ont été annulées
        if (response.data.data.cancelled_count > 0) {
          const count = response.data.data.cancelled_count;
          showAlert(
            `${count} réservation${count > 1 ? 's' : ''} expirée${count > 1 ? 's' : ''} annulée${count > 1 ? 's' : ''} automatiquement`,
            'info'
          );
        }
      }
    } catch (error) {
      // Fail silently pour ne pas perturber l'UX - log seulement en développement
      if (process.env.NODE_ENV === 'development') {
        console.log('Vérification automatique des réservations expirées:', error.message);
      }
    }
  };

  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleRoomClick = async (roomId) => {
    try {
      const response = await axios.get(`${API_BASE}/rooms/${roomId}`);
      setSelectedRoom(response.data);
    } catch (error) {
      showAlert('Erreur lors du chargement des détails de la chambre', 'error');
    }
  };

  const handleCheckIn = async (reservationId) => {
    try {
      await axios.put(`${API_BASE}/reservations/${reservationId}/checkin`);
      showAlert('Check-in effectué avec succès !');

      // UX optimisée : Mise à jour locale immédiate
      // 1. Mettre à jour les réservations localement
      setReservations(prevReservations =>
        prevReservations.map(reservation =>
          reservation.id === reservationId
            ? { ...reservation, status: 'checked_in' }
            : reservation
        )
      );

      // 2. Mettre à jour la chambre si modal ouverte
      if (selectedRoom && selectedRoom.reservation_id === reservationId) {
        const updatedRoom = { ...selectedRoom, status: 'occupied' };
        handleRoomUpdate(updatedRoom);
        setSelectedRoom(null);
      } else {
        // 3. Mettre à jour la chambre dans la liste si pas de modal
        const reservation = reservations.find(r => r.id === reservationId);
        if (reservation) {
          const roomToUpdate = rooms.find(room => room.id === reservation.room_id);
          if (roomToUpdate) {
            const updatedRoom = { ...roomToUpdate, status: 'occupied' };
            handleRoomUpdate(updatedRoom);
          }
        }
      }
    } catch (error) {
      showAlert('Erreur lors du check-in', 'error');
    }
  };

  const handleCheckOut = async (reservationId) => {
    try {
      await axios.put(`${API_BASE}/reservations/${reservationId}/checkout`);
      showAlert('Check-out effectué avec succès !');

      // UX optimisée : Mise à jour locale immédiate
      // 1. Mettre à jour les réservations localement
      setReservations(prevReservations =>
        prevReservations.map(reservation =>
          reservation.id === reservationId
            ? { ...reservation, status: 'checked_out' }
            : reservation
        )
      );

      // 2. Mettre à jour la chambre si modal ouverte
      if (selectedRoom && selectedRoom.reservation_id === reservationId) {
        const updatedRoom = {
          ...selectedRoom,
          status: 'cleaning',  // Passer en nettoyage après check-out
          guest_name: null,
          guest_email: null,
          guest_phone: null,
          reservation_id: null
        };
        handleRoomUpdate(updatedRoom);
        setSelectedRoom(null);
      } else {
        // 3. Mettre à jour la chambre dans la liste si pas de modal
        const reservation = reservations.find(r => r.id === reservationId);
        if (reservation) {
          const roomToUpdate = rooms.find(room => room.id === reservation.room_id);
          if (roomToUpdate) {
            const updatedRoom = {
              ...roomToUpdate,
              status: 'cleaning',
              guest_name: null,
              guest_email: null,
              guest_phone: null,
              reservation_id: null
            };
            handleRoomUpdate(updatedRoom);
          }
        }
      }
    } catch (error) {
      showAlert('Erreur lors du check-out', 'error');
    }
  };

  const handleNewReservation = async (reservationData) => {
    try {
      await axios.post(`${API_BASE}/reservations`, reservationData);
      showAlert('Réservation créée avec succès !');
      setPreselectedRoomId(null); // Réinitialiser la présélection

      // UX optimisée : Mise à jour locale immédiate
      const roomToUpdate = rooms.find(room => room.id === reservationData.room_id);
      if (roomToUpdate) {
        const updatedRoom = { ...roomToUpdate, status: 'reserved' };
        handleRoomUpdate(updatedRoom);
      }

      // Rediriger vers la liste des réservations après succès (cohérence UX)
      setCurrentView('reservations');
    } catch (error) {
      showAlert('Erreur lors de la création de la réservation', 'error');
    }
  };

  const handleCancelReservation = async (reservationId) => {
    if (window.confirm('Êtes-vous sûr de vouloir annuler cette réservation ?')) {
      try {
        await axios.put(`${API_BASE}/reservations/${reservationId}/cancel`, {
          reason: 'Annulation manuelle'
        });
        showAlert('Réservation annulée avec succès !');

        // UX optimisée : Mise à jour locale immédiate
        // 1. Mettre à jour les réservations localement
        setReservations(prevReservations =>
          prevReservations.map(reservation =>
            reservation.id === reservationId
              ? { ...reservation, status: 'cancelled' }
              : reservation
          )
        );

        // 2. Fermer la modal si ouverte (plus simple et plus robuste)
        if (selectedRoom) {
          setSelectedRoom(null);
        }

        // 3. Mettre à jour la chambre dans la liste
        const reservation = reservations.find(r => r.id === reservationId);
        if (reservation) {
          const roomToUpdate = rooms.find(room => room.id === reservation.room_id);
          if (roomToUpdate) {
            const updatedRoom = {
              ...roomToUpdate,
              status: 'available',
              guest_name: null,
              guest_email: null,
              guest_phone: null,
              reservation_id: null
            };
            handleRoomUpdate(updatedRoom);
          }
        }
      } catch (error) {
        showAlert('Erreur lors de l\'annulation', 'error');
      }
    }
  };

  // UX optimisée : Mise à jour locale des chambres sans reload
  const handleRoomUpdate = (updatedRoom) => {
    // Mettre à jour le state local des rooms
    setRooms(prevRooms =>
      prevRooms.map(room =>
        room.id === updatedRoom.id ? updatedRoom : room
      )
    );

    // Mettre à jour les chambres filtrées
    setFilteredRooms(prevFiltered =>
      prevFiltered.map(room =>
        room.id === updatedRoom.id ? updatedRoom : room
      )
    );

    // Mettre à jour selectedRoom si c'est la même chambre
    setSelectedRoom(updatedRoom);

    // Recharger les stats pour refléter les changements
    axios.get(`${API_BASE}/stats/dashboard`)
      .then(statsRes => setStats(statsRes.data))
      .catch(error => console.error('Erreur lors du rechargement des stats:', error));
  };

  // UX optimisée : Gestion locale de l'ajout de charges
  const handleChargeAdded = (newCharge) => {
    // Pas de reload - la modal gère déjà localement l'ajout dans son state
    // Cette fonction pourrait être étendue pour mettre à jour d'autres parties de l'UI si nécessaire
    console.log('Charge ajoutée:', newCharge);
  };

  const handleFilterChange = (filtered, filters) => {
    setFilteredRooms(filtered);
    setActiveFilters(filters);
  };

  const handleQuickReserve = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    if (!room) return;

    // Stocker l'ID de la chambre présélectionnée
    setPreselectedRoomId(roomId);
    // Naviguer vers le formulaire de nouvelle réservation
    setCurrentView('new-reservation');
  };

  const handleLogin = async (userData, token) => {

    // Mettre à jour l'authService avec les nouvelles données
    authService.token = token;
    authService.user = userData;
    authService.setupInterceptors();

    // Mettre à jour l'état de l'utilisateur
    setUser(userData);
    setIsAuthenticated(true);

    // Charger les données après avoir mis à jour l'état
    try {
      await loadData();
    } catch (error) {
      console.error('Error loading data after login:', error);
    }
  };

  const handleLogout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    setRooms([]);
    setReservations([]);
    setStats({});
    setCurrentView('dashboard');
  };

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  const renderNavigation = () => (
    <nav className="nav">
      <a
        href="#"
        className={`nav-item ${currentView === 'dashboard' ? 'active' : ''}`}
        onClick={(e) => { e.preventDefault(); setCurrentView('dashboard'); }}
      >
        <span className="nav-icon">📊</span>
        <span className="nav-label">Dashboard</span>
      </a>
      <a
        href="#"
        className={`nav-item ${currentView === 'rooms' ? 'active' : ''}`}
        onClick={(e) => { e.preventDefault(); setCurrentView('rooms'); }}
      >
        <span className="nav-icon">🚪</span>
        <span className="nav-label">Chambres</span>
      </a>
      <a
        href="#"
        className={`nav-item ${currentView === 'reservations' ? 'active' : ''}`}
        onClick={(e) => { e.preventDefault(); setCurrentView('reservations'); }}
      >
        <span className="nav-icon">📋</span>
        <span className="nav-label">Réservations</span>
      </a>
      <a
        href="#"
        className={`nav-item ${currentView === 'agenda' ? 'active' : ''}`}
        onClick={(e) => { e.preventDefault(); setCurrentView('agenda'); }}
      >
        <span className="nav-icon">📅</span>
        <span className="nav-label">Agenda</span>
      </a>
    </nav>
  );

  const renderDatabaseError = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      padding: '2rem',
      textAlign: 'center'
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
        border: '2px solid #dc2626',
        borderRadius: '16px',
        padding: '3rem',
        maxWidth: '600px',
        boxShadow: '0 10px 25px rgba(220, 38, 38, 0.1)'
      }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔧</div>
        <h2 style={{ color: '#dc2626', marginBottom: '1rem' }}>
          Problème de Base de Données
        </h2>
        <p style={{ color: '#7f1d1d', marginBottom: '1.5rem', fontSize: '1.1rem' }}>
          {databaseError?.message || 'La base de données n\'est pas accessible'}
        </p>

        <div style={{
          background: 'rgba(255, 255, 255, 0.8)',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '2rem',
          textAlign: 'left'
        }}>
          <h4 style={{ color: '#991b1b', marginBottom: '0.5rem' }}>Détails Techniques:</h4>
          <p style={{ color: '#7f1d1d', fontSize: '0.9rem', fontFamily: 'monospace' }}>
            {databaseError?.details || 'Erreur inconnue'}
          </p>
        </div>

        <div style={{
          background: 'rgba(255, 255, 255, 0.8)',
          padding: '1.5rem',
          borderRadius: '8px',
          textAlign: 'left'
        }}>
          <h4 style={{ color: '#991b1b', marginBottom: '1rem' }}>Solutions:</h4>
          <ul style={{ color: '#7f1d1d', paddingLeft: '1.5rem' }}>
            <li>Vérifiez que le serveur backend est démarré</li>
            <li>Si corruption détectée, supprimez hotel.db et redémarrez</li>
            <li>Vérifiez les permissions du fichier de base de données</li>
            <li>Consultez les logs du serveur pour plus de détails</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    if (loading) {
      return <div className="loading">Chargement...</div>;
    }

    if (databaseError) {
      return renderDatabaseError();
    }

    switch (currentView) {
      case 'dashboard':
        return (
          <div className="dashboard-layout">
            <div className="dashboard-top">
              <Dashboard />
            </div>
            <div className="dashboard-bottom">
              <SimpleDashboardGrid
                rooms={rooms}
                onRoomClick={handleRoomClick}
                onNavigate={setCurrentView}
              />
            </div>
          </div>
        );
      case 'rooms':
        return (
          <div className="rooms-layout">
            <RoomFilters
              rooms={rooms}
              onFilterChange={handleFilterChange}
            />

            <RoomGrid
              rooms={filteredRooms}
              onRoomClick={handleRoomClick}
              onQuickReservation={(room) => handleQuickReserve(room.id)}
              showLegend={true}
            />
          </div>
        );
      case 'reservations':
        return (
          <ReservationList
            reservations={reservations}
            onCancel={handleCancelReservation}
            onCheckIn={handleCheckIn}
            onCheckOut={handleCheckOut}
            onNewReservation={() => setCurrentView('new-reservation')}
          />
        );
      case 'agenda':
        return (
          <Agenda
            user={user}
          />
        );
      case 'checkinout':
        return (
          <CheckInOut
            reservations={reservations.filter(r => r.status === 'confirmed' || r.status === 'checked_in')}
            onCheckIn={handleCheckIn}
            onCheckOut={handleCheckOut}
            totalRooms={totalRooms}
          />
        );
      case 'new-reservation':
        return (
          <NewReservation
            rooms={rooms.filter(r => r.status === 'available')}
            onSubmit={handleNewReservation}
            onCancel={() => {
              setCurrentView('reservations');
              setPreselectedRoomId(null); // Réinitialiser la présélection
            }}
            preselectedRoomId={preselectedRoomId}
          />
        );
      default:
        return <div>Page non trouvée</div>;
    }
  };

  return (
    <div className="App">
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="header-brand">
              <div className="brand-icon">
                <img
                  src="https://www.royalandilana.com/wp-content/uploads/2023/01/Logo-Royal-Andilana-Blanc_Plan-de-travail-1-copie.png"
                  alt="Royal Andilana Resort & Spa"
                  style={{
                    height: '40px',
                    maxWidth: '100%',
                    objectFit: 'contain'
                  }}
                />
              </div>
              <div className="brand-info">
                <h1 className="brand-title">Royal Andilana Resort & Spa</h1>
                <span className="brand-subtitle">Système de Gestion Hôtelière</span>
              </div>
            </div>

            <div className="header-user">
              <div className="user-info">
                <span className="user-welcome">Bienvenue,</span>
                <span className="user-name">{user?.username}</span>
              </div>
              <button
                className="logout-btn"
                onClick={handleLogout}
                title="Déconnexion"
              >
                <span className="logout-icon">👤</span>
                <span className="logout-text">Déconnexion</span>
              </button>
            </div>
          </div>

          {renderNavigation()}
        </div>
      </header>

      <main className="container">
        {alert && (
          <div className={`alert alert-${alert.type}`}>
            {alert.message}
          </div>
        )}

        {renderContent()}
      </main>

      {selectedRoom && (
        <RoomDetails
          room={selectedRoom}
          onClose={() => setSelectedRoom(null)}
          onCheckIn={handleCheckIn}
          onCheckOut={handleCheckOut}
          onQuickReservation={handleQuickReserve}
          onCancelReservation={handleCancelReservation}
          onRoomUpdate={handleRoomUpdate}
          onChargeAdded={handleChargeAdded}
          showAlert={showAlert}
        />
      )}
    </div>
  );
}

export default App;